import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddIcon from "@mui/icons-material/Add";
import ChatIcon from "@mui/icons-material/Chat";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import MessageIcon from "@mui/icons-material/Message";
import SendIcon from "@mui/icons-material/Send";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableHead,
  Typography,
} from "@mui/material";
import List from "@mui/material/List";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "../Button/Button";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import {
  CommentCell,
  CommentsHistoryPopover,
  DataCell,
  DataRow,
  HeaderBox,
  HeaderCell,
  HeaderRow,
  MainContainer,
  NumberInput,
  StyledDropdown,
  StyledMenu,
  StyledTableContainer,
  TotalCell,
  TotalHoursBox,
  TotalRow,
  WeekSelector,
  closeButtonStyles,
  commentPopoverProps,
  getWeekDates,
} from "./TimesheetTableStyles";

const hasSelectedId = (value) =>
  value != null && String(value).trim() !== "";

const hasWeeklyTimesheetId = (row) =>
  row?.weekly_timesheet_id != null &&
  String(row.weekly_timesheet_id).trim() !== "";

const TimesheetTable = ({
  data,
  onAdd,
  onDelete,
  onSave,
  onSaveTimesheet,
  projects,
  fetchActivitiesForProject,
  fetchMilestoneActivityMappings,
  handleSubmit,
  fridays,
  selectedFriday,
  onFridayChange,
  onDateChange,
  isDraft,
  onProjectChange,
  onError,
  lastSavedDate,
  holidays,
}) => {
  const [rowActivities, setRowActivities] = useState({});
  const [rowMilestoneActivities, setRowMilestoneActivities] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [weekDates, setWeekDates] = useState([]);
  const [commentAnchorEl, setCommentAnchorEl] = useState(null);
  const [activeCommentRow, setActiveCommentRow] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState(null);
  const [commentsAnchorEl, setCommentsAnchorEl] = useState(null);
  const [activeComments, setActiveComments] = useState(null);
  const [commentHistoryAnchorEl, setCommentHistoryAnchorEl] = useState(null);
  const [activeCommentHistory, setActiveCommentHistory] = useState([]);
  const [activeComment, setActiveComment] = useState({
    rowId: null,
    comment: "",
  });

  const projectOptions = useMemo(
    () =>
      projects.map((p) => ({
        value: p.project_id.toString(),
        label: p.project_name,
      })),
    [projects]
  );
  const formatTimeValue = (value) => {
    if (!value || value === "0") return "0";

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "0";

    const hours = Math.floor(numValue);
    const minutes = Math.round((numValue - hours) * 100);

    const additionalHours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    const totalHours = hours + additionalHours;

    if (remainingMinutes === 0) {
      return totalHours.toString();
    } else {
      return `${totalHours}.${remainingMinutes.toString().padStart(2, "0")}`;
    }
  };

  const calculateRowTotal = useCallback((row) => {
    const days = [
      "saturday",
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
    ];

    let totalMinutes = 0;
    days.forEach((day) => {
      const value = Number(row[day] || 0);
      const hours = Math.floor(value);
      const minutes = Math.round((value - hours) * 100);
      totalMinutes += hours * 60 + minutes;
    });

    const totalHours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;
    if (remainingMinutes === 0) {
      return totalHours.toString();
    } else {
      return `${totalHours}.${remainingMinutes.toString().padStart(2, "0")}`;
    }
  }, []);

  const calculateGrandTotal = () => {
    const total = data.reduce((sum, row) => {
      const days = [
        "saturday",
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
      ];

      let totalMinutes = 0;
      days.forEach((day) => {
        const value = Number(row[day] || 0);
        const hours = Math.floor(value);
        const minutes = Math.round((value - hours) * 100);
        totalMinutes += hours * 60 + minutes;
      });

      return sum + totalMinutes;
    }, 0);

    const totalHours = Math.floor(total / 60);
    const remainingMinutes = total % 60;

    if (remainingMinutes === 0) {
      return totalHours.toString();
    } else {
      return `${totalHours}.${remainingMinutes.toString().padStart(2, "0")}`;
    }
  };

  const calculateColumnTotalInMinutes = (day) => {
    const totalMinutes = data.reduce((sum, row) => {
      const value = Number(row[day] || 0);
      const hours = Math.floor(value);
      const minutes = Math.round((value - hours) * 100);
      return sum + (hours * 60 + minutes);
    }, 0);

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (minutes === 0) {
      return hours.toString();
    } else {
      return `${hours}.${minutes.toString().padStart(2, "0")}`;
    }
  };

  const isProjectActivityCombinationExists = useCallback(
    (projectId, activityId, milestoneActivityMappingId, currentRowId) => {
      return data.some(
        (row) =>
          row.id !== currentRowId &&
          row.project_id === projectId &&
          row.activity_id === activityId &&
          row.milestone_activity_mapping_id === milestoneActivityMappingId
      );
    },
    [data]
  );

  const handleProjectChange = async (rowIndex, value) => {
    const days = [
      "saturday",
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
    ];
    const dayValues = {};
   days.forEach((day) => {
      dayValues[day] = "0";
    });

    const updatedRow = {
      ...data[rowIndex],
      project_id: value,
      activity_id: "",
      milestone_activity_mapping_id: "",
      actuals_budget: "0/0",
      ...dayValues,
      total_hours: "0",
      comments: "",
      resource_comments: [],
      project_manager_comments: [],
    };

    onProjectChange(rowIndex, value);

    if (value) {
      const activities = await fetchActivitiesForProject(parseInt(value));
      setRowActivities((prev) => ({
        ...prev,
        [data[rowIndex].id]: activities,
      }));
    } else {
      setRowActivities((prev) => ({
        ...prev,
        [data[rowIndex].id]: [],
      }));
    }

    // Clear milestone activity mappings when project changes
    setRowMilestoneActivities((prev) => ({
      ...prev,
      [data[rowIndex].id]: [],
    }));

    onSave(updatedRow);
  };

  const handleActivityChange = useCallback(
    async (rowId, activityId) => {
      if (!rowId || !activityId) return;

      const currentRow = data.find((row) => row.id === rowId);
      if (!currentRow) return;

      // Check for duplicate combination only if milestone activity is selected
      if (
        currentRow.milestone_activity_mapping_id &&
        isProjectActivityCombinationExists(
          currentRow.project_id,
          activityId,
          currentRow.milestone_activity_mapping_id,
          rowId
        )
      ) {
        onError({
          type: "error",
          title: "Validation Error",
          message:
            "This project, milestone and activity combination already exists in another row.",
        });
        return;
      }

      const activities = rowActivities[rowId];
      if (!activities) return;
      const activity = activities.find(
        (act) => act.value.toString() === activityId.toString()
      );
      if (activity) {
        // Fetch milestone activity mappings for this project and activity
        const milestoneActivities = await fetchMilestoneActivityMappings(
          currentRow.project_id,
          activityId
        );

        setRowMilestoneActivities((prev) => ({
          ...prev,
          [rowId]: milestoneActivities,
        }));

        // Check if it's a holiday project
        const selectedProject = projectOptions.find(
          (p) => p.value === currentRow.project_id
        );
        const isHolidayProject = selectedProject?.label
          ?.toLowerCase()
          .includes("leave & holiday");
        const isCompanyHolidayActivity = activity.label
          ?.toLowerCase()
          .includes("company holiday");
        const shouldAutoFillHoliday = isHolidayProject && isCompanyHolidayActivity;

        // Set values for the days
        const days = [
          "saturday",
          "sunday",
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
        ];
        const dayValues = {};
        days.forEach((day, index) => {
          const dayDate = weekDates[index]?.fullDate;
          // Auto-fill 8 hours only for company holiday activity on holiday dates
          if (shouldAutoFillHoliday && dayDate && isHoliday(dayDate)) {
            dayValues[day] = "8";
          } else {
            dayValues[day] = "0";
          }
        });

        onSave({
          ...currentRow,
          activity_id: activityId.toString(),
          milestone_activity_mapping_id: "", // Reset milestone activity mapping
          actuals_budget: `${activity.resource_actual_hours}/${
            activity.resource_budget_hours || 0
          }`,
          ...dayValues,
          total_hours: "0",
          comments: "",
          resource_comments: [],
          project_manager_comments: [],
        });
      }
    },
    [
      data,
      rowActivities,
      onSave,
      isProjectActivityCombinationExists,
      onError,
      fetchMilestoneActivityMappings,
    ]
  );

  const handleMilestoneActivityMappingChange = useCallback(
    (rowId, mappingId) => {
      if (!rowId || !mappingId) return;

      const currentRow = data.find((row) => row.id === rowId);
      if (!currentRow) return;

      const milestoneActivities = rowMilestoneActivities[rowId];
      if (!milestoneActivities) return;

      // Check for duplicate combination before allowing the change
      if (
        isProjectActivityCombinationExists(
          currentRow.project_id,
          currentRow.activity_id,
          mappingId,
          rowId
        )
      ) {
        onError({
          type: "error",
          title: "Validation Error",
          message:
            "This project, milestone and activity combination already exists in another row.",
        });
        return;
      }

      const mapping = milestoneActivities.find(
        (map) => map.value.toString() === mappingId.toString()
      );

      if (mapping) {
        // Check if it's a holiday project
        const selectedProject = projectOptions.find(
          (p) => p.value === currentRow.project_id
        );
        const isHolidayProject = selectedProject?.label
          ?.toLowerCase()
          .includes("leave & holiday");
        const activitiesForRow = rowActivities[currentRow.id] || [];
        const currentActivity = activitiesForRow.find(
          (act) =>
            act.value.toString() === currentRow.activity_id?.toString()
        );
        const isCompanyHolidayActivity = currentActivity?.label
          ?.toLowerCase()
          .includes("company holiday");
        const shouldAutoFillHoliday = isHolidayProject && isCompanyHolidayActivity;

        // Create updated row, preserving holiday hours
        const days = [
          "saturday",
          "sunday",
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
        ];
        const dayValues = {};
        days.forEach((day, index) => {
          const dayDate = weekDates[index]?.fullDate;
          // Preserve 8 hours only for company holiday activity on holiday dates
          if (shouldAutoFillHoliday && dayDate && isHoliday(dayDate)) {
            dayValues[day] = "8";
          } else {
            dayValues[day] = currentRow[day] || "0";
          }
        });

        onSave({
          ...currentRow,
          milestone_activity_mapping_id: mappingId.toString(),
          ...dayValues,
        });
      }
    },
    [
      data,
      rowMilestoneActivities,
      rowActivities,
      onSave,
      projectOptions,
      weekDates,
      isProjectActivityCombinationExists,
      onError,
    ]
  );

  useEffect(() => {
    const loadActivities = async () => {
      const newActivities = {};
      const promises = data
        .filter((row) => row.project_id && !rowActivities[row.id])
        .map(async (row) => {
          const activities = await fetchActivitiesForProject(
            parseInt(row.project_id)
          );
          newActivities[row.id] = activities;
        });

      await Promise.all(promises);
      setRowActivities((prev) => ({
        ...prev,
        ...newActivities,
      }));
    };

    loadActivities();
  }, [data, fetchActivitiesForProject]);

  useEffect(() => {
    const loadMilestoneActivities = async () => {
      const newMilestoneActivities = {};
      const promises = data
        .filter(
          (row) =>
            row.project_id && row.activity_id && !rowMilestoneActivities[row.id]
        )
        .map(async (row) => {
          const milestoneActivities = await fetchMilestoneActivityMappings(
            parseInt(row.project_id),
            row.activity_id
          );
          newMilestoneActivities[row.id] = milestoneActivities;
        });

      await Promise.all(promises);
      setRowMilestoneActivities((prev) => ({
        ...prev,
        ...newMilestoneActivities,
      }));
    };

    loadMilestoneActivities();
  }, [data, fetchMilestoneActivityMappings]);

  useEffect(() => {
    if (selectedFriday?.value) {
      try {
        const dates = getWeekDates(selectedFriday.value);
        if (dates && dates.length > 0) {
          setWeekDates(dates);
        } else {
          console.warn(
            "No valid week dates generated for:",
            selectedFriday.value
          );
          setWeekDates([]);
        }
      } catch (error) {
        console.error("Error setting week dates:", error);
        setWeekDates([]);
      }
    }
  }, [selectedFriday?.value]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFridaySelect = useCallback(
    (friday) => {
      if (friday?.value) {
        onDateChange(new Date(friday.value));
      }
      setAnchorEl(null);
    },
    [onDateChange]
  );

  const handleNextWeek = () => {
    const currentIndex = fridays.findIndex(
      (friday) => friday.value === selectedFriday?.value
    );
    if (currentIndex > 0) {
      const prevFriday = fridays[currentIndex - 1];
      onDateChange(new Date(prevFriday.value));
    }
  };

  const handlePreviousWeek = () => {
    const currentIndex = fridays.findIndex(
      (friday) => friday.value === selectedFriday?.value
    );
    if (currentIndex < fridays.length - 1) {
      const nextFriday = fridays[currentIndex + 1];
      onDateChange(new Date(nextFriday.value));
    }
  };

  const validateProjectActivityMilestoneSelection = useCallback(() => {
    const projectWithoutActivity = data.some(
      (row) => hasSelectedId(row.project_id) && !hasSelectedId(row.activity_id)
    );
    const projectActivityWithoutMilestone = data.some(
      (row) =>
        hasSelectedId(row.project_id) &&
        hasSelectedId(row.activity_id) &&
        !hasSelectedId(row.milestone_activity_mapping_id)
    );

    if (projectWithoutActivity && projectActivityWithoutMilestone) {
      onError({
        type: "error",
        title: "Validation Error",
        message:
          "Please select an Activity for every row where a Project is selected, and a Milestone Activity for every row where Project and Activity are selected.",
      });
      return false;
    }

    if (projectWithoutActivity) {
      onError({
        type: "error",
        title: "Validation Error",
        message:
          "Please select an Milestone for every row where a Project is selected.",
      });
      return false;
    }

    if (projectActivityWithoutMilestone) {
      onError({
        type: "error",
        title: "Validation Error",
        message:
          "Please select a Activity for every row where Project and Milestone are selected.",
      });
      return false;
    }

    return true;
  }, [data, onError]);

  const validateNoZeroHourPersistedRows = useCallback(() => {
    const hasPersistedZeroHourRow = data.some((row) => {
      if (!hasWeeklyTimesheetId(row)) return false;
      return Number(calculateRowTotal(row)) === 0;
    });

    if (hasPersistedZeroHourRow) {
      onError({
        type: "error",
        title: "Validation Error",
        message:
          "You have an existing timesheet row with 0 hours. Please enter hours or delete the row before saving/submitting.",
      });
      return false;
    }

    return true;
  }, [data, onError, calculateRowTotal]);

  const handleSubmitTimesheet = async () => {
    if (!validateProjectActivityMilestoneSelection()) return;
    if (!validateNoZeroHourPersistedRows()) return;

    // Filter out rows with 0 total hours
    const nonZeroHoursData = data.filter((row) => {
      const rowTotal = calculateRowTotal(row);
      return rowTotal > 0;
    });

    // First validate if there are any hours logged
    const hasHours = nonZeroHoursData.some((row) => {
      const days = [
        "saturday",
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
      ];
      return days.some((day) => Number(row[day]) > 0);
    });

    if (!hasHours) {
      onError({
        type: "error",
        title: "Validation Error",
        message: "Please enter hours before submitting the timesheet.",
      });
      return;
    }

    // Check if there are any filled rows with all required fields
    const hasFilledRows = nonZeroHoursData.every((row) => {
      return (
        row.project_id && row.activity_id && row.milestone_activity_mapping_id
      );
    });

    if (!hasFilledRows) {
      onError({
        type: "error",
        title: "Validation Error",
        message:
          "Please fill in all required fields (Project, Milestone and Activity) before submitting.",
      });
      return;
    }

    // const totalWeeklyHours = nonZeroHoursData.reduce(
    //   (sum, row) => sum + parseFloat(calculateRowTotal(row)),
    //   0
    // );

    // Convert to number and round to 2 decimal places for accurate comparison
    // const totalHoursNumber = Number(totalWeeklyHours.toFixed(2));
    
    const totalHoursNumber = Number(calculateGrandTotal());

    // Validation: Weekly hours should not exceed 48
    if (totalHoursNumber > 48) {
      onError({
        type: "info",
        title: "Hours exceed weekly limit",
        message:
          "Please obtain leadership approval via email keeping your Project Manager and Resource Manager in CC to update the additional hours from the backend.\n\nIn the meantime, please submit your timesheet up to 48 hours for this week as mandatory compliance.",
      });
      return;
    }

    if (totalHoursNumber < 40) {
      onError({
        type: "error",
        title: "Validation Error",
        message: `Total hours for the week must be at least 40. Current total: ${totalHoursNumber} hours.`,
      });
      return;
    }

    // Only show confirmation modal if all validations pass
    setConfirmationModal({
      open: true,
      title: "Submit Timesheet",
      message:
        "Are you sure you want to submit this timesheet? This action cannot be undone.",
    });
  };

  // New function to handle the actual submission after confirmation
  const confirmSubmitTimesheet = async () => {
    try {
      setConfirmationModal(null); // Close modal before submission
      await handleSubmit(true);
    } catch (error) {
      console.error("Error submitting timesheet:", error);
      onError({
        type: "error",
        title: "Submission Error",
        message: "Failed to submit timesheet. Please try again.",
      });
    }
  };

  const handleSaveTimesheet = async () => {
    if (!validateProjectActivityMilestoneSelection()) return;
    if (!validateNoZeroHourPersistedRows()) return;

    // Validate required fields only for rows that have data
    const rowsWithData = data.filter((row) => {
      const hasHours = [
        "saturday",
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
      ].some((day) => Number(row[day]) > 0);
      return hasHours;
    });

    // Check if any row with hours is missing required fields
    const hasEmptyFields = rowsWithData.some((row) => {
      return (
        !row.project_id ||
        !row.activity_id ||
        !row.milestone_activity_mapping_id
      );
    });

    if (hasEmptyFields) {
      onError({
        type: "error",
        title: "Validation Error",
        message:
          "Please fill in all required fields (Project, Milestone and Activity) before saving.",
      });
      return;
    }

    try {
      await onSaveTimesheet();
    } catch (error) {
      console.error("Error saving timesheet:", error);
      onError({
        type: "error",
        title: "Save Error",
        message: "Failed to save timesheet. Please try again.",
      });
    }
  };

  // Memoize the current week selection
  const currentWeekLabel = useMemo(() => {
    return selectedFriday?.label || "Select Week";
  }, [selectedFriday]);

  const handleCommentClick = (event, row) => {
    setCommentAnchorEl(event.currentTarget);
    setActiveCommentRow(row);
  };

  const handleCommentClose = () => {
    setCommentAnchorEl(null);
    setActiveCommentRow(null);
  };

  const handleCommentChange = (event) => {
    if (activeCommentRow && isDraft) {
      const updatedRow = {
        ...activeCommentRow,
        comments: event.target.value,
      };
      setActiveCommentRow(updatedRow);
      onSave(updatedRow);
    }
  };

  // Function to format the last saved time without moment
  const getLastSavedText = (lastSavedDate) => {
    if (!lastSavedDate) return "Not saved yet";

    try {
      // Convert millisecond timestamp to Date object
      const now = new Date();
      const savedTime = new Date(parseInt(lastSavedDate));

      // Check if date is valid
      if (isNaN(savedTime.getTime())) {
        console.error("Invalid timestamp:", lastSavedDate);
        return "Invalid date";
      }

      const diffMs = now - savedTime;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins} minutes ago`;
      if (diffHours < 24) return `${diffHours} hours ago`;
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 7) return `${diffDays} days ago`;

      // Format date for older entries
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      };

      return savedTime.toLocaleDateString("en-US", options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Date error";
    }
  };

  const getBudgetHours = (budgetString) => {
    if (!budgetString) return 0;
    const parts = budgetString.split("/");
    return parseFloat(parts[1]) || 0;
  };

  const formatBudgetString = (budgetString) => {
    if (!budgetString) return "0/0";
    const parts = budgetString.split("/");
    const actual = parseFloat(parts[0]) || 0;
    const budget = parseFloat(parts[1]) || 0;

    const formatNumber = (num) => {
      if (num === 0) return "0";
      // Check if number has decimal places
      const hasDecimals = num % 1 !== 0;
      return hasDecimals ? num.toFixed(2) : num.toString();
    };

    return `${formatNumber(actual)}/${formatNumber(budget)}`;
  };

  const isOverBudget = (row) => {
    const totalHours = calculateRowTotal(row);
    const budgetHours = getBudgetHours(row.actuals_budget);
    return budgetHours > 0 && totalHours > budgetHours;
  };

  const handleHistoryClick = (event, row) => {
    event.stopPropagation();
    setCommentsAnchorEl(event.currentTarget);
    const allComments = [
      ...(row.project_manager_comments || []).map((comment) => ({
        ...comment,
        type: "manager",
        name: comment.resource_name,
        timestamp: comment.created_on,
      })),
      ...(row.resource_comments || []).map((comment) => ({
        ...comment,
        type: "resource",
        name: comment.resource_name,
        timestamp: comment.created_on,
      })),
    ].sort((a, b) => parseInt(a.timestamp) - parseInt(b.timestamp));

    setActiveComments(allComments);
  };

  const handleHistoryClose = () => {
    setCommentsAnchorEl(null);
    setActiveComments(null);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown time";

    try {
      const now = new Date();
      const commentTime = timestamp.includes("-")
        ? new Date(timestamp)
        : new Date(parseInt(timestamp));

      if (isNaN(commentTime.getTime())) {
        console.error("Invalid timestamp:", timestamp);
        return "Invalid date";
      }

      const diffMs = now - commentTime;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins} minutes ago`;
      if (diffHours < 24) return `${diffHours} hours ago`;
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 7) return `${diffDays} days ago`;

      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      };

      return commentTime.toLocaleDateString("en-US", options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Date error";
    }
  };

  const formatCommentDate = formatDate;

  // Helper function to parse date in DD/MM/YYYY format
  const parseDate = useCallback((dateString) => {
    if (!dateString) return null;

    try {
      // Handle DD/MM/YYYY format (from API)
      if (typeof dateString === "string" && dateString.includes("/")) {
        const parts = dateString.split("/");
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
          const year = parseInt(parts[2], 10);
          const date = new Date(year, month, day);
          date.setHours(0, 0, 0, 0);
          return date;
        }
      }

      // Handle other date formats
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        date.setHours(0, 0, 0, 0);
        return date;
      }
      return null;
    } catch (error) {
      console.error("Error parsing date:", dateString, error);
      return null;
    }
  }, []);

  // Function to check if a date is a holiday
  const isHoliday = useCallback(
    (date) => {
      if (!holidays || holidays.length === 0 || !date) return false;

      try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return false;

        // Set time to midnight to avoid timezone issues
        dateObj.setHours(0, 0, 0, 0);
        const dateString = dateObj.toISOString().split("T")[0];

        const isHolidayDate = holidays.some((holiday) => {
          try {
            const holidayDate = parseDate(holiday.holiday_date);
            if (!holidayDate) return false;

            const holidayDateString = holidayDate.toISOString().split("T")[0];
            const matches = holidayDateString === dateString;

            // Debug logging
            if (matches) {
              console.log("Holiday match found:", {
                checkingDate: dateString,
                holidayDate: holiday.holiday_date,
                holidayName: holiday.holiday_name,
                parsedHolidayDate: holidayDateString,
              });
            }

            return matches;
          } catch (error) {
            console.error("Error processing holiday date:", error);
            return false;
          }
        });

        return isHolidayDate;
      } catch (error) {
        console.error("Error checking holiday date:", error);
        return false;
      }
    },
    [holidays, parseDate]
  );

  // Function to get holiday name for a date
  const getHolidayName = useCallback(
    (date) => {
      if (!holidays || holidays.length === 0 || !date) return null;

      try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return null;

        const dateString = dateObj.toISOString().split("T")[0];

        const holiday = holidays.find((holiday) => {
          try {
            const holidayDate = parseDate(holiday.holiday_date);
            if (!holidayDate) return false;

            const holidayDateString = holidayDate.toISOString().split("T")[0];
            return holidayDateString === dateString;
          } catch (error) {
            console.error("Error processing holiday date:", error);
            return false;
          }
        });

        return holiday ? holiday.holiday_name : null;
      } catch (error) {
        console.error("Error getting holiday name:", error);
        return null;
      }
    },
    [holidays, parseDate]
  );

  const handleCommentHistoryClick = (e, row) => {
    e.stopPropagation();
    setCommentHistoryAnchorEl(e.currentTarget);
    setActiveComment({
      rowId: row.id,
      comment: "",
    });

    const allComments = [
      ...(row.project_manager_comments || []).map((comment) => ({
        ...comment,
        type: "manager",
        name: comment.resource_name,
        timestamp: comment.created_on,
      })),
      ...(row.resource_comments || []).map((comment) => ({
        ...comment,
        type: "resource",
        name: comment.resource_name,
        timestamp: comment.created_on,
      })),
    ].sort((a, b) => parseInt(a.timestamp) - parseInt(b.timestamp));

    setActiveCommentHistory(allComments);
  };

  const handleCommentHistoryClose = () => {
    setCommentHistoryAnchorEl(null);
    setActiveCommentHistory([]);
  };

  const handleKeyPress = (e, row, day) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCommentSend();
    } else if (e.key === "Tab") {
      e.preventDefault();
      const currentRowIndex = data.findIndex((r) => r.id === row.id);
      const days = [
        "saturday",
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
      ];
      const currentDayIndex = days.indexOf(day);

      // Set current field to "0" if empty before moving to next
      if (row[day] === "" || row[day] === undefined) {
        onSave({
          ...row,
          [day]: "0",
        });
      }

      // If we're at the last day of the current row
      if (currentDayIndex === days.length - 1) {
        // Move to the first day of the next row
        if (currentRowIndex < data.length - 1) {
          const nextRow = data[currentRowIndex + 1];
          const nextInput = document.querySelector(
            `input[data-row-id="${nextRow.id}"][data-day="saturday"]`
          );
          if (nextInput) {
            nextInput.focus();
          }
        }
      } else {
        // Move to the next day in the current row
        const nextDay = days[currentDayIndex + 1];
        const nextInput = document.querySelector(
          `input[data-row-id="${row.id}"][data-day="${nextDay}"]`
        );
        if (nextInput) {
          nextInput.focus();
        }
      }
    }
  };

  const handleCommentSend = () => {
    if (!activeComment.comment.trim()) return;

    const newComment = {
      comment: activeComment.comment,
      timestamp: Date.now().toString(),
      created_on: new Date().toISOString(),
      type: "resource",
      name: "You",
    };

    setActiveCommentHistory((prev) => [...prev, newComment]);

    const updatedRow = data.find((row) => row.id === activeComment.rowId);
    if (updatedRow) {
      const existingComments =
        updatedRow.resource_comments?.filter((comment) => comment.id) || [];
      const newComments =
        updatedRow.resource_comments?.filter((comment) => !comment.id) || [];

      const rowWithComment = {
        ...updatedRow,
        comments: activeComment.comment,
        resource_comments: [...existingComments, ...newComments, newComment],
      };
      onSave(rowWithComment);
    }

    setActiveComment((prev) => ({ ...prev, comment: "" }));
  };

  console.log("projectOptions", projectOptions, data);

  return (
    <Box
      sx={{
        padding: "0px",
        border: "1px solid #E0E0E0",
        borderRadius: "8px",
        marginBottom: "0px",
        backgroundColor: "#fff",
      }}
    >
      <MainContainer>
        <HeaderBox>
          <Box className="week-section">
            <Typography className="title">Week Ending</Typography>
            <WeekSelector>
              <Box
                className="week-dropdown"
                onClick={handleClick}
                sx={{ cursor: "pointer" }}
              >
                <Typography className="week-text">
                  {selectedFriday?.label || "Select Week"}
                </Typography>
                <KeyboardArrowDownIcon className="dropdown-arrow" />
              </Box>
              <StyledMenu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                {fridays.map((friday) => (
                  <MenuItem
                    key={friday.weekNumber}
                    onClick={() => handleFridaySelect(friday)}
                    selected={selectedFriday?.weekNumber === friday.weekNumber}
                    sx={{
                      backgroundColor:
                        selectedFriday?.weekNumber === friday.weekNumber
                          ? "#F5F5F5"
                          : "inherit",
                      "&:hover": {
                        backgroundColor:
                          selectedFriday?.weekNumber === friday.weekNumber
                            ? "#EEEEEE"
                            : "#F5F5F5",
                      },
                    }}
                  >
                    {friday.label}
                  </MenuItem>
                ))}
              </StyledMenu>
              <Button
                className="nav-button left-arrow"
                variant="text"
                onClick={handlePreviousWeek}
                disabled={
                  !selectedFriday ||
                  fridays.indexOf(selectedFriday) === fridays.length - 1
                }
              >
                <KeyboardArrowLeftIcon />
              </Button>
              <Button
                className="nav-button right-arrow"
                variant="text"
                onClick={handleNextWeek}
                disabled={
                  !selectedFriday || fridays.indexOf(selectedFriday) === 0
                }
              >
                <KeyboardArrowRightIcon />
              </Button>
            </WeekSelector>
          </Box>

          <Box className="button-group">
            {isDraft && (
              <>
                <Button
                  variant="outlined"
                  onClick={handleSaveTimesheet}
                  size="small"
                  disabled={!selectedFriday || data.length === 0}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmitTimesheet}
                  size="small"
                  disabled={!selectedFriday || data.length === 0}
                >
                  Submit
                </Button>
              </>
            )}
          </Box>
        </HeaderBox>

        <StyledTableContainer component={Paper}>
          <Table
            size="small"
            sx={{
              tableLayout: "fixed",
              "& .MuiTableHead-root": {
                marginBottom: "0",
                "& .MuiTableRow-root": {
                  borderTopLeftRadius: "6px",
                  borderTopRightRadius: "6px",
                  overflow: "hidden",
                },
              },
              "& .MuiTableBody-root": {
                "& .MuiTableRow-root": {
                  marginBottom: "0",
                  "&:last-child": {
                    marginBottom: 0,
                  },
                },
              },
              borderCollapse: "separate",
              borderSpacing: "0",
            }}
          >
            <TableHead>
              <HeaderRow>
                <HeaderCell
                  style={{
                    width: "140px",
                    minWidth: "140px",
                    maxWidth: "140px",
                  }}
                >
                  PROJECT
                </HeaderCell>
                <HeaderCell style={{ width: "150px" }}>MILESTONE</HeaderCell>
                <HeaderCell style={{ width: "100px" }}>
                  ACTUAL / BUDGET
                </HeaderCell>
                <HeaderCell style={{ width: "180px" }}>ACTIVITY</HeaderCell>
                {weekDates.map((dateInfo, index) => (
                  <HeaderCell key={index} className="date-cell">
                    <Box
                      sx={{
                        height: "100%",
                        backgroundColor: "#5F80F6",
                        marginTop: "3px",
                        marginBottom: "3px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        position: "relative",
                        ...(index === 0 && {
                          borderTopLeftRadius: "4px",
                          borderBottomLeftRadius: "4px",
                        }),
                        ...(index === 6 && {
                          borderTopRightRadius: "4px",
                          borderBottomRightRadius: "4px",
                        }),
                      }}
                    >
                      <span
                        className="day"
                        style={{
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {dateInfo.day}
                      </span>
                      <span className="date">{dateInfo.date}</span>
                    </Box>
                  </HeaderCell>
                ))}
                <HeaderCell style={{ width: "70px", textAlign: "center" }}>
                  TOTAL HOURS
                </HeaderCell>
                <HeaderCell style={{ width: "100px", textAlign: "center" }}>
                  ACTION
                </HeaderCell>
              </HeaderRow>
            </TableHead>
            <TableBody>
              {data.map((row, index) => (
                <DataRow key={row.id}>
                  <DataCell>
                    <StyledDropdown
                      value={row.project_id || ""}
                      onChange={(value) => handleProjectChange(index, value)}
                      options={projectOptions}
                      placeholder="Select Project"
                      fullWidth
                      disabled={!row.is_draft}
                      sx={{
                        "& .MuiSelect-select": {
                          textAlign: "center",
                          paddingRight: "24px !important",
                        },
                      }}
                    />
                  </DataCell>
                  <DataCell>
                    <StyledDropdown
                      value={row.activity_id || ""}
                      onChange={(value) => handleActivityChange(row.id, value)}
                      options={rowActivities[row.id] || []}
                      placeholder="Select Milestone"
                      fullWidth
                      disabled={!row.is_draft || !row.project_id}
                    />
                  </DataCell>
                  <DataCell className="budget-cell">
                    <span className="budget-text">
                      {formatBudgetString(row.actuals_budget)}
                    </span>
                  </DataCell>
                  <DataCell>
                    <StyledDropdown
                      value={row.milestone_activity_mapping_id || ""}
                      onChange={(value) =>
                        handleMilestoneActivityMappingChange(row.id, value)
                      }
                      options={
                        row.activity_id
                          ? rowMilestoneActivities[row.id] || []
                          : []
                      }
                      placeholder="Select Activity"
                      fullWidth
                      disabled={
                        !row.is_draft || !row.project_id || !row.activity_id
                      }
                    />
                  </DataCell>
                  {[
                    "saturday",
                    "sunday",
                    "monday",
                    "tuesday",
                    "wednesday",
                    "thursday",
                    "friday",
                  ].map((day, dayIndex) => {
                    const dayDate = weekDates[dayIndex]?.fullDate;
                    const isHolidayDate =
                      dayDate && !isNaN(dayDate.getTime())
                        ? isHoliday(dayDate)
                        : false;
                    const holidayName =
                      dayDate && !isNaN(dayDate.getTime())
                        ? getHolidayName(dayDate)
                        : null;
                  const isWeekend = day === "saturday" || day === "sunday";

                    // Debug logging for holiday detection
                    if (isHolidayDate) {
                      console.log("Holiday detected in input cell:", {
                        day,
                        dayDate: dayDate?.toISOString().split("T")[0],
                        holidayName,
                        dayIndex,
                      });
                    }

                    return (
                      <DataCell
                        key={day}
                        className="day-cell"
                        sx={{ position: "relative" }}
                      >
                        <NumberInput
                          type="number"
                          value={
                            row[day] === undefined || row[day] === ""
                              ? "0"
                              : row[day]
                          }
                            onChange={(e) => {
                              if (!row.is_draft || isWeekend) return;
                              let value = e.target.value;

                            // If the current value is '0' and user types a digit (not a decimal), replace '0' with that digit
                            if (
                              (row[day] === undefined ||
                                row[day] === "0" ||
                                row[day] === 0) &&
                              value.length === 2 &&
                              value.startsWith("0") &&
                              value[1] !== "."
                            ) {
                              value = value[1];
                            }
                            // Prevent leading zeros except for decimals (e.g., '0.5' is allowed, '04' is not)
                            if (/^0\d+/.test(value)) {
                              value = value.replace(/^0+/, "");
                            }

                            // Allow up to 2 digits before decimal, 2 after
                            const regex = /^\d{0,2}(\.\d{0,2})?$/;
                            if (!regex.test(value)) {
                              return;
                            }

                            // Convert to number for range validation
                            const numValue = parseFloat(value);
                            if (
                              value === "" ||
                              (!isNaN(numValue) &&
                                numValue >= 0 &&
                                numValue <= 20)
                            ) {
                              onSave({
                                ...row,
                                [day]: value === "" ? "0" : value,
                              });
                            }
                          }}
                          onKeyDown={(e) => handleKeyPress(e, row, day)}
                          disabled={!row.is_draft || isWeekend}
                          onFocus={(e) => {
                            if (e.target.value === "0") {
                              onSave({ ...row, [day]: "" });
                            }
                          }}
                          onBlur={(e) => {
                            if (e.target.value === "") {
                              onSave({ ...row, [day]: "0" });
                            } else {
                              const value = parseFloat(e.target.value || 0);
                              const formattedValue = formatTimeValue(value);
                              onSave({ ...row, [day]: formattedValue });
                            }
                          }}
                          fullWidth
                          sx={{
                            ...(isHolidayDate && {
                              "& .MuiInputBase-root": {
                                backgroundColor: "rgb(197, 208, 249)",
                                border: "1px solid rgb(197, 208, 249)",
                                borderRadius: "4px",
                              },
                              "& .MuiInputBase-input": {
                                color: "#D32F2F",
                                fontWeight: "bold",
                              },
                            }),
                          }}
                          inputProps={{
                            min: 0,
                            max: 20,
                            step: "0.01",
                            style: {
                              textAlign: "center",
                              fontSize: "11px",
                              padding: "1px",
                              backgroundColor: "transparent",
                              height: "100%",
                            },
                            "data-row-id": row.id,
                            "data-day": day,
                            title: isHolidayDate ? holidayName : undefined,
                          }}
                        />
                        {isHolidayDate && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: "2px",
                              right: "2px",
                              fontSize: "8px",
                              color: "#D32F2F",
                              backgroundColor: "rgba(255, 107, 107, 0.2)",
                              borderRadius: "2px",
                              padding: "1px 2px",
                              pointerEvents: "none",
                            }}
                          ></Box>
                        )}
                      </DataCell>
                    );
                  })}
                  <DataCell align="center">
                    <TotalHoursBox
                      sx={{
                        ...(isOverBudget(row) && {
                          border: "2px solid #d32f2f",
                          backgroundColor: "rgba(211, 47, 47, 0.04)",
                        }),
                      }}
                    >
                      {calculateRowTotal(row)}
                    </TotalHoursBox>
                  </DataCell>
                  <DataCell className="action-cell">
                    <Tooltip title="View Comments" placement="top" arrow>
                      <div
                        className="comment-icon-wrapper"
                        onClick={(e) => handleCommentHistoryClick(e, row)}
                        style={{
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                      >
                        {(row.resource_comments &&
                          row.resource_comments.length > 0) ||
                        (row.project_manager_comments &&
                          row.project_manager_comments.length > 0) ? (
                          <MessageIcon
                            className="comment-icon has-comment"
                            fontSize="small"
                            sx={{ color: "#3F49A7" }}
                          />
                        ) : (
                          <ChatBubbleOutlineIcon
                            className="comment-icon"
                            fontSize="small"
                            sx={{ color: "rgba(63, 73, 167, 0.5)" }}
                          />
                        )}
                      </div>
                    </Tooltip>
                    {row.is_draft && (
                      <IconButton size="small" onClick={() => onDelete(row)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </DataCell>
                </DataRow>
              ))}
              <TotalRow>
                <TotalCell colSpan={4}>
                  <Box className="add-row-section">
                    {isDraft && (
                      <IconButton size="small" onClick={onAdd}>
                        <AddIcon />
                      </IconButton>
                    )}
                    <Typography>
                      {lastSavedDate
                        ? !isDraft
                          ? `Last Submited ${getLastSavedText(lastSavedDate)}`
                          : `Last Saved ${getLastSavedText(lastSavedDate)}`
                        : "Not saved yet"}
                    </Typography>
                  </Box>
                </TotalCell>
                {[
                  "saturday",
                  "sunday",
                  "monday",
                  "tuesday",
                  "wednesday",
                  "thursday",
                  "friday",
                ].map((day) => (
                  <TotalCell key={day}>
                    <span className="total-value">
                      {calculateColumnTotalInMinutes(day)}
                    </span>
                  </TotalCell>
                ))}
                <TotalCell>
                  <span className="total-value">{calculateGrandTotal()}</span>
                </TotalCell>
                <TotalCell />
              </TotalRow>
            </TableBody>
          </Table>
        </StyledTableContainer>
      </MainContainer>

      {/* Add the confirmation modal */}
      {confirmationModal && (
        <ConfirmationModal
          open={confirmationModal.open}
          title={confirmationModal.title}
          message={confirmationModal.message}
          onClose={() => setConfirmationModal(null)}
          onConfirm={confirmSubmitTimesheet}
          buttonText={"Submit Timesheet"}
          colorCode={"#2E7D32"}
        />
      )}

      {/* Add the comment history popover */}
      <Popover
        open={Boolean(commentHistoryAnchorEl)}
        anchorEl={commentHistoryAnchorEl}
        onClose={handleCommentHistoryClose}
        anchorOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
        PaperProps={commentPopoverProps}
      >
        <CommentsHistoryPopover>
          <div className="comments-header">
            <Typography variant="h6">
              <MessageIcon className="icon" />
              Comments
            </Typography>
            <IconButton
              size="small"
              onClick={handleCommentHistoryClose}
              sx={closeButtonStyles}
            >
              <CloseIcon sx={{ fontSize: "12px" }} />
            </IconButton>
          </div>
          <List className="comment-list" disablePadding>
            {activeCommentHistory?.map((comment, index) => (
              <div
                key={`${comment.timestamp}-${index}`}
                className={`comment-bubble ${comment.type}`}
              >
                <Typography className="comment-text">
                  {comment.comment}
                </Typography>
                <div className="comment-meta">
                  <span className={`user-name ${comment.type}-name`}>
                    {comment.type === "manager" ? (
                      <SupervisorAccountIcon className="user-icon" />
                    ) : (
                      <ChatIcon className="user-icon" />
                    )}
                    {comment.name}
                  </span>
                  <span className="comment-date">
                    <AccessTimeIcon className="time-icon" />
                    {formatCommentDate(comment.timestamp)}
                  </span>
                </div>
              </div>
            ))}
            {(!activeCommentHistory || activeCommentHistory.length === 0) && (
              <div className="no-comments">
                <ChatBubbleOutlineIcon className="icon" />
                <Typography className="text">
                  No comments available for this timesheet entry
                </Typography>
              </div>
            )}
          </List>
          <div className="comment-input-container">
            <TextField
              className="comment-input"
              multiline
              maxRows={4}
              placeholder="Type a comment..."
              value={activeComment.comment}
              onChange={(e) =>
                setActiveComment((prev) => ({
                  ...prev,
                  comment: e.target.value,
                }))
              }
              onKeyPress={handleKeyPress}
              variant="outlined"
              fullWidth
              disabled={!isDraft}
            />
            <IconButton
              className="send-button"
              onClick={handleCommentSend}
              disabled={!activeComment.comment.trim() || !isDraft}
            >
              <SendIcon
                sx={{
                  fontSize: "16px",
                  color: "#FFFFFF",
                  transform: "rotate(-45deg)",
                }}
              />
            </IconButton>
          </div>
        </CommentsHistoryPopover>
      </Popover>
    </Box>
  );
};

export default React.memo(TimesheetTable);
